import React, { useState, useMemo, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import logger from '../utils/logger';
import { 
  Target, 
  Users, 
  Mail, 
  Send,
  MousePointer, 
  MessageSquare, 
  TrendingUp, 
  Eye,
  Edit,
  Plus,
  Search,
  Filter,
  Activity,
  Trash2,
  X,
  RefreshCw
} from 'lucide-react';
import { useData } from '../contexts/DataContext';
import { useRBACData } from '../hooks/useRBACData';
import { useRBAC } from '../contexts/RBACContext';
import { LEAD_STATUS } from '../config/supabase';

const Campaigns = () => {
  // Use RBAC-filtered data for role-based access
  const { leads: rbacLeads, leadEvents: rbacLeadEvents, getAllowedCampaigns, isAdminOrHigher, assignedCampaigns } = useRBACData();
  const { canDelete } = useRBAC();
  const { getCampaignStatsRealtime } = useData();
  const leads = rbacLeads || [];
  const leadEvents = rbacLeadEvents || [];
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState('lastActivity');
  const [sortOrder, setSortOrder] = useState('desc');
  const [selectedCampaign, setSelectedCampaign] = useState(null);
  const [editingCampaign, setEditingCampaign] = useState(null);
  const [deletingCampaign, setDeletingCampaign] = useState(null);
  const [editForm, setEditForm] = useState({ name: '', description: '' });
  const [realtimeCampaignData, setRealtimeCampaignData] = useState(null);
  const [loadingRealtime, setLoadingRealtime] = useState(true);
  const [useRealtime, setUseRealtime] = useState(true);

  // Track component mount to ensure fresh data load
  const isMountedRef = useRef(true);

  // Load real-time campaign data on component mount and when leadEvents changes
  useEffect(() => {
    // Reset mounted state when component mounts
    isMountedRef.current = true;
    
    // Load data when leadEvents are available
    if (leadEvents && leadEvents.length > 0) {
      loadRealtimeCampaignData();
    } else {
      // If no events, still set loading to false and use fallback
      setLoadingRealtime(false);
      setUseRealtime(false);
    }
    
    // Cleanup on unmount
    return () => {
      isMountedRef.current = false;
    };
  }, [leadEvents]);

  // Function to load real-time campaign data
  const loadRealtimeCampaignData = async () => {
    if (!getCampaignStatsRealtime) {
      logger.warn('getCampaignStatsRealtime function not available, falling back to client-side calculation');
      setUseRealtime(false);
      setLoadingRealtime(false);
      return;
    }

    try {
      setLoadingRealtime(true);
      // IMPORTANT: Pass leadEvents to ensure consistent reply counting logic
      const realtimeData = await getCampaignStatsRealtime(leadEvents);
      
      if (realtimeData && Array.isArray(realtimeData) && realtimeData.length > 0) {
        setRealtimeCampaignData(realtimeData);
        setUseRealtime(true);
        // Data loaded successfully
      } else {
        // Falling back to client-side calculation
        setUseRealtime(false);
      }
    } catch (error) {
      logger.error('Error loading real-time campaign data:', error);
      setUseRealtime(false);
    } finally {
      setLoadingRealtime(false);
    }
  };

  // Process campaign data with comprehensive statistics (fallback method)
  const campaignData = useMemo(() => {
    const campaignStats = leads.reduce((acc, lead) => {
      const campaign = lead.campaign_name || 'Unknown Campaign';
      if (!acc[campaign]) {
        acc[campaign] = {
          name: campaign,
          leads: 0,
          totalScore: 0,
          avgScore: 0,
          newLeads: 0,
          interestedLeads: 0,
          callListLeads: 0,
          archivedLeads: 0,
          opens: 0,
          clicks: 0,
          replies: 0,
          sent: 0,
          lastActivity: null,
          createdAt: null
        };
      }
      
      acc[campaign].leads += 1;
      acc[campaign].totalScore += lead.score || 0;
      
      // Count by status
      switch (lead.status) {
        case LEAD_STATUS.NEW:
          acc[campaign].newLeads += 1;
          break;
        case LEAD_STATUS.INTERESTED:
          acc[campaign].interestedLeads += 1;
          break;
        case LEAD_STATUS.CALL_LIST:
          acc[campaign].callListLeads += 1;
          break;
        case LEAD_STATUS.ARCHIVED:
          acc[campaign].archivedLeads += 1;
          break;
        default:
          acc[campaign].newLeads += 1;
          break;
      }
      
      // Track creation date
      if (!acc[campaign].createdAt || new Date(lead.created_at) < new Date(acc[campaign].createdAt)) {
        acc[campaign].createdAt = lead.created_at;
      }
      
      // Track last activity
      if (lead.last_event_date && (!acc[campaign].lastActivity || new Date(lead.last_event_date) > new Date(acc[campaign].lastActivity))) {
        acc[campaign].lastActivity = lead.last_event_date;
      }
      
      return acc;
    }, {});
    
    // Add event statistics - count unique events per lead
    const campaignEventStats = {};
    
    leadEvents.forEach(event => {
      const campaign = event.campaign_name || 'Unknown Campaign';
      const leadEmail = event.lead_email;
      
      if (!campaignEventStats[campaign]) {
        campaignEventStats[campaign] = {
          uniqueOpens: new Set(),
          uniqueClicks: new Set(),
          uniqueReplies: new Set(),
          uniqueSent: new Set(),
          totalSent: 0
        };
      }
      
      switch (event.event_type) {
        case 'open':
          campaignEventStats[campaign].uniqueOpens.add(leadEmail);
          break;
        case 'click':
          campaignEventStats[campaign].uniqueClicks.add(leadEmail);
          break;
        case 'reply':
          // Only count replies with actual reply message content
          if (event.reply_text && event.reply_text.trim().length > 0) {
            campaignEventStats[campaign].uniqueReplies.add(leadEmail);
          }
          break;
        case 'sent':
        case 'email_sent':
          if (leadEmail) campaignEventStats[campaign].uniqueSent.add(leadEmail);
          campaignEventStats[campaign].totalSent += 1;
          break;
      }
    });
    
    // Apply unique event counts to campaign stats
    Object.keys(campaignEventStats).forEach(campaign => {
      if (campaignStats[campaign]) {
        campaignStats[campaign].opens = campaignEventStats[campaign].uniqueOpens.size;
        campaignStats[campaign].clicks = campaignEventStats[campaign].uniqueClicks.size;
        campaignStats[campaign].replies = campaignEventStats[campaign].uniqueReplies.size;
        campaignStats[campaign].sent = campaignEventStats[campaign].totalSent;
        campaignStats[campaign].sentLeads = campaignEventStats[campaign].uniqueSent.size;
       }
     });
    
    // Calculate average scores
    Object.values(campaignStats).forEach(campaign => {
      campaign.avgScore = campaign.leads > 0 ? Math.round(campaign.totalScore / campaign.leads) : 0;
    });
    
    return Object.values(campaignStats);
  }, [leads, leadEvents]);

  // Filter and sort campaigns - use real-time data when available
  const filteredCampaigns = useMemo(() => {
    // Use real-time data if available, otherwise fall back to client-side calculation
    let dataSource = useRealtime && realtimeCampaignData ? realtimeCampaignData : campaignData;
    
    // CRITICAL: Apply RBAC filtering to ensure only assigned campaigns are shown
    // For non-admin users, filter to only show campaigns they are assigned to
    if (!isAdminOrHigher() && assignedCampaigns && assignedCampaigns.length > 0) {
      dataSource = dataSource.filter(campaign => 
        assignedCampaigns.includes(campaign.name)
      );
    }
    
    let filtered = [...dataSource];
    
    if (searchTerm) {
      const search = searchTerm.toLowerCase();
      filtered = filtered.filter(campaign => 
        campaign.name.toLowerCase().includes(search)
      );
    }
    
    // Sort campaigns
    filtered.sort((a, b) => {
      let aValue = a[sortBy];
      let bValue = b[sortBy];
      
      if (sortBy === 'createdAt' || sortBy === 'lastActivity') {
        aValue = new Date(aValue || 0);
        bValue = new Date(bValue || 0);
      }
      
      if (sortOrder === 'asc') {
        return aValue > bValue ? 1 : -1;
      } else {
        return aValue < bValue ? 1 : -1;
      }
    });
    
    return filtered;
  }, [campaignData, realtimeCampaignData, useRealtime, searchTerm, sortBy, sortOrder, isAdminOrHigher, assignedCampaigns]);

  const handleSort = (field) => {
    if (sortBy === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(field);
      setSortOrder('desc');
    }
  };

  const handleEditCampaign = (campaign) => {
    setEditingCampaign(campaign);
    setEditForm({ name: campaign.name, description: campaign.description || '' });
  };

  const handleSaveEdit = () => {
    // Saving campaign edit
    setEditingCampaign(null);
    setEditForm({ name: '', description: '' });
  };

  const handleDeleteCampaign = (campaign) => {
    setDeletingCampaign(campaign);
  };

  const confirmDelete = () => {
    // Deleting campaign
    setDeletingCampaign(null);
  };

  const CampaignCard = ({ campaign }) => {
    const conversionRate = campaign.leads > 0 ? Math.min(((campaign.replies / campaign.leads) * 100), 99.9).toFixed(1) : 0;
    const engagementRate = campaign.leads > 0 ? Math.min((((campaign.opens + campaign.clicks) / campaign.leads) * 100), 99.9).toFixed(1) : 0;
    const openRateDenominator = (campaign.sentLeads && campaign.sentLeads > 0) ? campaign.sentLeads : (campaign.sent || 0);
    const openRate = openRateDenominator > 0 ? Math.min(((campaign.opens / openRateDenominator) * 100), 99.9).toFixed(1) : 0;
    
    return (
      <div className="bg-blue-50 p-4 rounded-lg shadow-sm border border-blue-100 hover:shadow-md transition-shadow">
        <div className="flex items-start justify-between mb-3">
          <div className="flex items-center space-x-2">
            <div className="w-8 h-8 bg-blue-500 rounded-lg flex items-center justify-center">
              <Target className="w-4 h-4 text-white" />
            </div>
            <div>
              <h3 className="text-base font-semibold text-gray-900">{campaign.name}</h3>
              <p className="text-xs text-gray-500">
                Created {campaign.createdAt ? new Date(campaign.createdAt).toLocaleDateString() : 'Unknown'}
              </p>
            </div>
          </div>
          <div className="flex items-center space-x-1">
            <button
              onClick={() => setSelectedCampaign(campaign)}
              className="p-1.5 text-blue-500 hover:text-blue-700 hover:bg-blue-100 rounded-md transition-colors"
              title="View Details"
            >
              <Eye className="w-3.5 h-3.5" />
            </button>
            <button
              onClick={() => window.dispatchEvent(new CustomEvent('viewCampaignLeads', { detail: { campaignName: campaign.name } }))}
              className="p-1.5 text-blue-500 hover:text-blue-700 hover:bg-blue-100 rounded-md transition-colors"
              title="View Campaign Leads"
            >
              <Users className="w-3.5 h-3.5" />
            </button>
            <button
              onClick={() => handleEditCampaign(campaign)}
              className="p-1.5 text-blue-500 hover:text-blue-700 hover:bg-blue-100 rounded-md transition-colors"
              title="Edit Campaign"
            >
              <Edit className="w-3.5 h-3.5" />
            </button>
            {canDelete() && (
              <button
                onClick={() => handleDeleteCampaign(campaign)}
                className="p-1.5 text-red-500 hover:text-red-700 hover:bg-red-100 rounded-md transition-colors"
                title="Delete Campaign"
              >
                <Trash2 className="w-3.5 h-3.5" />
              </button>
            )}
          </div>
        </div>
        
        {/* Key Metrics */}
        <div className="grid grid-cols-5 gap-2 mb-3">
          <div className="text-center bg-white rounded-md p-2">
            <div className="text-lg font-bold text-blue-600">{campaign.leads}</div>
            <div className="text-xs text-gray-500">Leads</div>
          </div>
          <div className="text-center bg-white rounded-md p-2">
            <div className="text-lg font-bold text-blue-600">{campaign.sent || 0}</div>
            <div className="text-xs text-gray-500">Sent</div>
          </div>
          <div className="text-center bg-white rounded-md p-2">
            <div className="text-lg font-bold text-blue-600">{campaign.avgScore}</div>
            <div className="text-xs text-gray-500">Score</div>
          </div>
          <div className="text-center bg-white rounded-md p-2">
            <div className="text-lg font-bold text-blue-600">{conversionRate}%</div>
            <div className="text-xs text-gray-500">Reply</div>
            <div className="text-xs font-medium text-blue-500">{campaign.replies}</div>
          </div>
          <div className="text-center bg-white rounded-md p-2">
            <div className="text-lg font-bold text-blue-600">{openRate}%</div>
            <div className="text-xs text-gray-500">Open</div>
            <div className="text-xs font-medium text-blue-500">{campaign.opens}</div>
          </div>
        </div>
        
        {/* Status Distribution */}
        <div className="grid grid-cols-4 gap-1.5 mb-3">
          <div className="text-center p-1.5 bg-white rounded-md">
            <div className="text-sm font-semibold text-blue-600">{campaign.newLeads}</div>
            <div className="text-xs text-gray-500">New</div>
          </div>
          <div className="text-center p-1.5 bg-white rounded-md">
            <div className="text-sm font-semibold text-blue-600">{campaign.interestedLeads}</div>
            <div className="text-xs text-gray-500">Interest</div>
          </div>
          <div className="text-center p-1.5 bg-white rounded-md">
            <div className="text-sm font-semibold text-blue-600">{campaign.callListLeads}</div>
            <div className="text-xs text-gray-500">Call</div>
          </div>
          <div className="text-center p-1.5 bg-white rounded-md">
            <div className="text-sm font-semibold text-blue-600">{campaign.archivedLeads}</div>
            <div className="text-xs text-gray-500">Archive</div>
          </div>
        </div>
        
        {/* Activity Stats */}
        <div className="flex items-center justify-between text-xs text-gray-600 bg-white rounded-md p-2">
          <div className="flex items-center space-x-3">
            <span className="flex items-center space-x-1" title="Emails Sent">
              <Send className="w-3 h-3 text-blue-500" />
              <span>{campaign.sent || 0}</span>
            </span>
            <span className="flex items-center space-x-1" title="Opens">
              <Mail className="w-3 h-3 text-blue-500" />
              <span>{campaign.opens}</span>
            </span>
            <span className="flex items-center space-x-1" title="Clicks">
              <MousePointer className="w-3 h-3 text-blue-500" />
              <span>{campaign.clicks}</span>
            </span>
            <span className="flex items-center space-x-1" title="Replies">
              <MessageSquare className="w-3 h-3 text-blue-500" />
              <span>{campaign.replies}</span>
            </span>
          </div>
          <div className="text-xs text-gray-500">
            {campaign.lastActivity ? new Date(campaign.lastActivity).toLocaleDateString() : 'No activity'}
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Campaign Management</h1>
          <p className="text-gray-600">Monitor and manage all your marketing campaigns in one place.</p>
        </div>
      </div>

      {/* Stats Overview */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="bg-blue-50 p-4 rounded-lg shadow-sm border border-blue-100">
          <div className="flex items-center">
            <div className="w-10 h-10 bg-blue-500 rounded-lg flex items-center justify-center">
              <Target className="w-5 h-5 text-white" />
            </div>
            <div className="ml-3">
              <p className="text-sm font-medium text-gray-600">Total Campaigns</p>
              <p className="text-xl font-bold text-blue-600">{filteredCampaigns.length}</p>
            </div>
          </div>
        </div>
        
        <div className="bg-blue-50 p-4 rounded-lg shadow-sm border border-blue-100">
          <div className="flex items-center">
            <div className="w-10 h-10 bg-blue-500 rounded-lg flex items-center justify-center">
              <Users className="w-5 h-5 text-white" />
            </div>
            <div className="ml-3">
              <p className="text-sm font-medium text-gray-600">Total Leads</p>
              <p className="text-xl font-bold text-blue-600">{filteredCampaigns.reduce((sum, c) => sum + (c.leads || 0), 0)}</p>
            </div>
          </div>
        </div>
        
        <div className="bg-blue-50 p-4 rounded-lg shadow-sm border border-blue-100">
          <div className="flex items-center">
            <div className="w-10 h-10 bg-blue-500 rounded-lg flex items-center justify-center">
              <MessageSquare className="w-5 h-5 text-white" />
            </div>
            <div className="ml-3">
              <p className="text-sm font-medium text-gray-600">Total Replies</p>
              <p className="text-xl font-bold text-blue-600">{filteredCampaigns.reduce((sum, c) => sum + (c.replies || 0), 0)}</p>
            </div>
          </div>
        </div>
        
        <div className="bg-blue-50 p-4 rounded-lg shadow-sm border border-blue-100">
          <div className="flex items-center">
            <div className="w-10 h-10 bg-blue-500 rounded-lg flex items-center justify-center">
              <Activity className="w-5 h-5 text-white" />
            </div>
            <div className="ml-3">
              <p className="text-sm font-medium text-gray-600">Avg Engagement</p>
              <p className="text-xl font-bold text-blue-600">
                {filteredCampaigns.length > 0 ? 
                  Math.round(filteredCampaigns.reduce((sum, c) => sum + ((c.opens + c.clicks) / Math.max(c.leads, 1)), 0) / filteredCampaigns.length * 100) : 0}%
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Search and Filters */}
      <div className="bg-blue-50 p-4 rounded-lg shadow-sm border border-blue-100">
        <div className="flex flex-col sm:flex-row gap-3">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-blue-400 w-4 h-4" />
            <input
              type="text"
              placeholder="Search campaigns..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-9 pr-3 py-2 border border-blue-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors bg-white placeholder-blue-400 text-gray-900 text-sm"
            />
          </div>
          <div className="flex items-center space-x-2">
            <Filter className="w-4 h-4 text-blue-500" />
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              className="px-3 py-2 border border-blue-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white text-gray-900 text-sm"
            >
              <option value="leads">Sort by Leads</option>
              <option value="avgScore">Sort by Avg Score</option>
              <option value="replies">Sort by Replies</option>
              <option value="createdAt">Sort by Created Date</option>
              <option value="lastActivity">Sort by Last Activity</option>
            </select>
            <button
              onClick={loadRealtimeCampaignData}
              disabled={loadingRealtime}
              className="px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 flex items-center space-x-2 text-sm"
              title="Refresh campaign data"
            >
              <RefreshCw className={`w-4 h-4 ${loadingRealtime ? 'animate-spin' : ''}`} />
              <span>Refresh</span>
            </button>
          </div>
        </div>
        
        {/* Data source indicator */}
        <div className="mt-2 flex items-center justify-between text-xs">
          <div className="flex items-center space-x-2">
            <div className={`w-2 h-2 rounded-full ${useRealtime ? 'bg-green-500' : 'bg-yellow-500'}`}></div>
            <span className="text-gray-600">
              {loadingRealtime ? 'Loading...' : useRealtime ? 'Real-time data' : 'Client-side calculation'}
            </span>
          </div>
          <span className="text-gray-500">
            {filteredCampaigns.length} campaign{filteredCampaigns.length !== 1 ? 's' : ''}
          </span>
        </div>
      </div>

      {/* Campaigns Grid */}
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
        {filteredCampaigns.map((campaign) => (
          <CampaignCard key={campaign.name} campaign={campaign} />
        ))}
      </div>

      {filteredCampaigns.length === 0 && (
        <div className="text-center py-16">
          <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <Target className="w-8 h-8 text-gray-400" />
          </div>
          <h3 className="text-xl font-semibold text-gray-900 mb-3">No campaigns found</h3>
          <p className="text-gray-500 text-lg max-w-md mx-auto">
            {searchTerm ? 'Try adjusting your search terms.' : 'Create your first campaign to get started.'}
          </p>
        </div>
      )}

      {/* Campaign Detail Modal */}
      <AnimatePresence>
        {selectedCampaign && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50"
            onClick={() => setSelectedCampaign(null)}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="p-6 border-b border-gray-200">
                <div className="flex items-center justify-between">
                  <h2 className="text-xl font-bold text-gray-900">{selectedCampaign.name}</h2>
                  <button
                    onClick={() => setSelectedCampaign(null)}
                    className="p-2 text-gray-400 hover:text-gray-600 rounded-lg transition-colors"
                  >
                    <X className="w-5 h-5" />
                  </button>
                </div>
              </div>
              
              <div className="p-6">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
                  <div className="text-center p-4 bg-blue-50 rounded-lg">
                    <div className="text-2xl font-bold text-blue-600 mb-2">{selectedCampaign.leads}</div>
                    <div className="text-sm text-blue-500">Total Leads</div>
                  </div>
                  <div className="text-center p-4 bg-green-50 rounded-lg">
                    <div className="text-2xl font-bold text-green-600 mb-2">{selectedCampaign.replies}</div>
                    <div className="text-sm text-green-500">Replies</div>
                  </div>
                  <div className="text-center p-4 bg-orange-50 rounded-lg">
                    <div className="text-2xl font-bold text-orange-600 mb-2">{selectedCampaign.opens}</div>
                    <div className="text-sm text-orange-500">Opens</div>
                  </div>
                  <div className="text-center p-4 bg-purple-50 rounded-lg">
                    <div className="text-2xl font-bold text-purple-600 mb-2">{selectedCampaign.clicks}</div>
                    <div className="text-sm text-purple-500">Clicks</div>
                  </div>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-4">Lead Status Distribution</h3>
                    <div className="space-y-3">
                      <div className="flex items-center justify-between p-3 bg-blue-50 rounded-lg">
                        <span className="text-blue-700 font-medium">New Leads</span>
                        <span className="text-blue-600 font-bold">{selectedCampaign.newLeads}</span>
                      </div>
                      <div className="flex items-center justify-between p-3 bg-green-50 rounded-lg">
                        <span className="text-green-700 font-medium">Interested</span>
                        <span className="text-green-600 font-bold">{selectedCampaign.interestedLeads}</span>
                      </div>
                      <div className="flex items-center justify-between p-3 bg-orange-50 rounded-lg">
                        <span className="text-orange-700 font-medium">Call List</span>
                        <span className="text-orange-600 font-bold">{selectedCampaign.callListLeads}</span>
                      </div>
                      <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                        <span className="text-gray-700 font-medium">Archived</span>
                        <span className="text-gray-600 font-bold">{selectedCampaign.archivedLeads}</span>
                      </div>
                    </div>
                  </div>
                  
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-4">Campaign Details</h3>
                    <div className="space-y-3">
                      <div className="flex items-center justify-between p-3 bg-blue-50 rounded-lg">
                        <span className="text-blue-700 font-medium">Opens</span>
                        <span className="text-blue-600 font-bold">{selectedCampaign.opens}</span>
                      </div>
                      <div className="flex items-center justify-between p-3 bg-blue-50 rounded-lg">
                        <span className="text-blue-700 font-medium">Replies</span>
                        <span className="text-blue-600 font-bold">{selectedCampaign.replies}</span>
                      </div>
                      <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                        <span className="text-gray-700 font-medium">Average Score</span>
                        <span className="text-gray-900 font-bold">{selectedCampaign.avgScore}</span>
                      </div>
                      <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                        <span className="text-gray-700 font-medium">Reply Rate</span>
                        <span className="text-gray-900 font-bold">
                          {selectedCampaign.leads > 0 ? ((selectedCampaign.replies / selectedCampaign.leads) * 100).toFixed(1) : 0}%
                        </span>
                      </div>
                      <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                        <span className="text-gray-700 font-medium">Engagement Rate</span>
                        <span className="text-gray-900 font-bold">
                          {selectedCampaign.leads > 0 ? (((selectedCampaign.opens + selectedCampaign.clicks) / selectedCampaign.leads) * 100).toFixed(1) : 0}%
                        </span>
                      </div>
                      <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                        <span className="text-gray-700 font-medium">Created</span>
                        <span className="text-gray-900 font-bold">
                          {selectedCampaign.createdAt ? new Date(selectedCampaign.createdAt).toLocaleDateString() : 'Unknown'}
                        </span>
                      </div>
                      <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                        <span className="text-gray-700 font-medium">Last Activity</span>
                        <span className="text-gray-900 font-bold">
                          {selectedCampaign.lastActivity ? new Date(selectedCampaign.lastActivity).toLocaleDateString() : 'None'}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Edit Campaign Modal */}
      <AnimatePresence>
        {editingCampaign && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50"
            onClick={() => setEditingCampaign(null)}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
              className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4"
            >
              <div className="flex items-center justify-between p-6 border-b border-gray-200">
                <h2 className="text-xl font-bold text-gray-900">Edit Campaign</h2>
                <button
                  onClick={() => setEditingCampaign(null)}
                  className="p-2 text-gray-400 hover:text-gray-600 rounded-lg transition-colors"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
              <div className="p-6 space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Campaign Name
                  </label>
                  <input
                    type="text"
                    value={editForm.name}
                    onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="Enter campaign name"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Description
                  </label>
                  <textarea
                    value={editForm.description}
                    onChange={(e) => setEditForm({ ...editForm, description: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    rows="3"
                    placeholder="Enter campaign description"
                  />
                </div>
                <div className="flex items-center justify-end space-x-3 pt-4">
                  <button
                    onClick={() => setEditingCampaign(null)}
                    className="px-4 py-2 text-gray-600 hover:text-gray-800 transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleSaveEdit}
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                  >
                    Save Changes
                  </button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Delete Campaign Modal */}
      <AnimatePresence>
        {deletingCampaign && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50"
            onClick={() => setDeletingCampaign(null)}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
              className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4"
            >
              <div className="p-6">
                <div className="flex items-center justify-center w-12 h-12 mx-auto mb-4 bg-red-100 rounded-full">
                  <Trash2 className="w-6 h-6 text-red-600" />
                </div>
                <h2 className="text-xl font-bold text-gray-900 text-center mb-2">Delete Campaign</h2>
                <p className="text-gray-600 text-center mb-6">
                  Are you sure you want to delete "{deletingCampaign.name}"? This action cannot be undone.
                </p>
                <div className="flex items-center justify-center space-x-3">
                  <button
                    onClick={() => setDeletingCampaign(null)}
                    className="px-4 py-2 text-gray-600 hover:text-gray-800 transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={confirmDelete}
                    className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
                  >
                    Delete Campaign
                  </button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default Campaigns;